import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { getOrderFulfillmentDetails } from "@/lib/order-fulfillment-details";
import type { FulfillmentChoice } from "@/lib/order-fulfillment-details";
import { convertDate } from "@/lib/utils";

type OrderFulfillmentDetailsProps = {
  locale: Locales;
  metadata: unknown;
};

const getChoiceLabel = (choice: FulfillmentChoice, locale: Locales) =>
  __(choice === "yes" ? "Yes" : "No", locale);

const OrderFulfillmentDetails = ({ locale, metadata }: OrderFulfillmentDetailsProps) => {
  const details = getOrderFulfillmentDetails(metadata);

  if (!details) {
    return null;
  }

  return (
    <section className="space-y-5">
      <h2 className="font-cabinet-grotesk text-2xl font-medium md:text-3xl">
        {__("Delivery and recovery", locale)}
      </h2>
      <div className="border-primary-light/30 rounded-md border p-4">
        <dl className="grid gap-2.5 md:grid-cols-2 md:gap-x-20">
          {details.wantsDelivery && (
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              <dt>{__("I would like a delivery?", locale)}</dt>
              <dd className="font-semibold">{getChoiceLabel(details.wantsDelivery, locale)}</dd>
            </div>
          )}
          {details.deliveryDate && (
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              <dt>{__("Items pickup date", locale)}</dt>
              <dd className="font-semibold capitalize">
                {convertDate(details.deliveryDate, "dd MMM yyyy", locale)}
              </dd>
            </div>
          )}
          {details.wantsRecovery && (
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              <dt>{__("I would like a recovery?", locale)}</dt>
              <dd className="font-semibold">{getChoiceLabel(details.wantsRecovery, locale)}</dd>
            </div>
          )}
          {details.recoveryDate && (
            <div className="flex flex-wrap items-center justify-between gap-1.5">
              <dt>{__("Items return date", locale)}</dt>
              <dd className="font-semibold capitalize">
                {convertDate(details.recoveryDate, "dd MMM yyyy", locale)}
              </dd>
            </div>
          )}
          {details.deliveryInstructions && (
            <div className="flex flex-wrap gap-1.5 md:col-span-2">
              <dt>{__("Delivery instructions", locale)}:</dt>
              <dd className="font-semibold whitespace-pre-wrap">{details.deliveryInstructions}</dd>
            </div>
          )}
        </dl>
      </div>
    </section>
  );
};

export default OrderFulfillmentDetails;
